import "./kendo.core.js";
import "./kendo.popup.js";
import "./kendo.toolbar.js";
import "./kendo.draganddrop.js";
import "./kendo.icons.js";

export const __meta__ = {
    id: "floatingtoolbar",
    name: "FloatingToolBar",
    category: "web",
    description: "The FloatingToolBar widget hosts a ToolBar inside a floating, positionable, optionally draggable container.",
    depends: ["core", "popup", "toolbar", "draganddrop", "icons"]
};

(function($, undefined) {
    const kendo = window.kendo;
    const Widget = kendo.ui.Widget;
    const ns = ".kendoFloatingToolBar";
    const keys = kendo.keys;

    const WIDGET_CLASS = "k-floating-toolbar";
    const DRAG_HANDLE_CLASS = "k-floating-toolbar-drag-handle";
    const MOVE_STEP = 10;
    const M_KEYCODE = 77;

    const ANCHOR_POSITION_MAP = {
        top: { origin: "top center", position: "bottom center" },
        bottom: { origin: "bottom center", position: "top center" },
        left: { origin: "center left", position: "center right" },
        right: { origin: "center right", position: "center left" }
    };

    function constrain(value, low, high) {
        return Math.min(Math.max(value, low), high);
    }

    function findPositionedAncestor(element) {
        let node = element.parent();

        while (node.length && node[0] !== document.body && node[0] !== document.documentElement) {
            const position = node.css("position");
            if (position === "relative" || position === "absolute" || position === "fixed" || position === "sticky") {
                return node;
            }
            node = node.parent();
        }

        return $(document.body);
    }

    const FloatingToolBar = Widget.extend({
        init: function(element, options) {
            const that = this;

            Widget.fn.init.call(that, element, options);

            that._pos = { top: 0, left: 0 };
            that._moveMode = false;
            that._container = findPositionedAncestor(that.element);

            that._wrapper();
            that._createDragHandle();
            that._createToolBar();
            that._createPopup();

            if (that.options.draggable) {
                that._createDraggable();
            }

            if (that.options.visible) {
                that.show();
            }

            kendo.notify(that);
        },

        options: {
            name: "FloatingToolBar",
            items: [],
            visible: false,
            anchor: undefined,
            position: "top",
            overflow: {
                mode: "menu",
                scrollButtons: "auto",
                scrollButtonsPosition: "split",
                scrollDistance: 200
            },
            draggable: false,
            size: undefined,
            fillMode: undefined,
            ariaLabel: undefined,
            containerAlign: {
                horizontal: "center",
                vertical: "top"
            },
            margin: {
                horizontal: 0,
                vertical: 0
            },
            messages: {
                dragHandle: "Drag to reposition",
                movedUp: "Moved up",
                movedDown: "Moved down",
                movedLeft: "Moved left",
                movedRight: "Moved right"
            }
        },

        events: [
            "show",
            "hide",
            "dragStart",
            "move",
            "dragEnd",
            "kendoKeydown"
        ],

        _wrapper: function() {
            const that = this;
            const element = that.element;

            that.wrapper = element;
            element.addClass(WIDGET_CLASS).hide();
            that._applyCssClasses();
        },

        _createToolBar: function() {
            const that = this;
            const options = that.options;
            const toolbarElement = $("<div></div>").appendTo(that.element);

            if (options.ariaLabel) {
                toolbarElement.attr("aria-label", options.ariaLabel);
            }

            that.toolbar = new kendo.ui.ToolBar(toolbarElement, {
                items: that._applyFillMode(options.items),
                overflow: options.overflow,
                size: options.size,
                fillMode: options.fillMode,
                resizable: !options.overflow || options.overflow.mode !== "none"
            });
        },

        _applyFillMode: function(items) {
            const fillMode = this.options.fillMode;

            if (!fillMode || !items) {
                return items;
            }

            return items.map(function(item) {
                if (item.type !== "component") {
                    return item;
                }

                const componentOptions = $.extend({ fillMode: fillMode }, item.componentOptions);

                return $.extend({}, item, { componentOptions: componentOptions });
            });
        },

        _createDragHandle: function() {
            const that = this;

            if (!that.options.draggable) {
                return;
            }

            const messages = that.options.messages;

            that.dragHandle = $("<span></span>")
                .addClass(DRAG_HANDLE_CLASS)
                .attr({
                    tabindex: 0,
                    role: "button",
                    "aria-label": messages.dragHandle,
                    "aria-pressed": "false"
                })
                .html(kendo.ui.icon("handle-drag-dots"))
                .prependTo(that.element);

            that._separator = $("<span class='k-separator k-separator-vertical'></span>")
                .insertAfter(that.dragHandle);

            that._liveRegion = $("<span class='k-sr-only'></span>")
                .attr("aria-live", "polite")
                .appendTo(that.element);

            that.element.on("keydown" + ns, that, that._keyDown.bind(that));
        },

        _openHandler: function(e) {
            const that = this;

            if (that.trigger("show")) {
                e.preventDefault();
                return;
            }

            that._previousActiveElement = document.activeElement;

            if (that.toolbar) {
                that.toolbar.resize(true);
            }
        },

        _closeHandler: function(e) {
            const that = this;

            if (that.trigger("hide")) {
                e.preventDefault();
                return;
            }

            const active = document.activeElement;
            const hadFocus = that.element[0] && $.contains(that.element[0], active);

            if (hadFocus && that._previousActiveElement) {
                $(that._previousActiveElement).trigger("focus");
            }
        },

        _createPopup: function() {
            const that = this;

            that.popup = new kendo.ui.Popup(that.element, {
                anchor: document.body,
                appendTo: that._container,
                origin: "top center",
                position: "top center",
                collision: "flip fit",
                autosize: true,
                open: that._openHandler.bind(that),
                close: that._closeHandler.bind(that),
                activate: function() {
                    that._refreshLayout();
                    that._pos = that._currentOffset();

                    $(document.documentElement).off(that.popup.downEvent, that.popup._mousedownProxy);
                }
            });

            that._patchPopupPosition();
            that._patchPopupScroll();
            that._configureMode();
        },

        _patchPopupScroll: function() {
            const that = this;
            const popup = that.popup;
            const originalResize = popup._resize.bind(popup);

            popup._resize = function(e) {
                if (e.type === "scroll") {
                    return;
                }

                originalResize(e);
            };
        },

        _patchPopupPosition: function() {
            const that = this;
            const popup = that.popup;
            const originalPosition = popup._position.bind(popup);

            popup._position = function(fixed) {
                if (!fixed && that._explicitPosition && popup.visible()) {
                    that._constrainToViewport();
                    return popup.flipped;
                }

                return originalPosition(fixed);
            };
        },

        _constrainToViewport: function() {
            const that = this;
            const wrapper = that.popup.wrapper;

            if (!wrapper || !wrapper.length) {
                return;
            }

            const bounds = that._viewportBounds();
            const pos = that._currentOffset();
            const top = constrain(pos.top, bounds.minTop, bounds.maxTop);
            const left = constrain(pos.left, bounds.minLeft, bounds.maxLeft);

            wrapper.css({ top: top, left: left });
            that._pos = { top: top, left: left };
        },

        _createDraggable: function() {
            const that = this;

            that._draggable = new kendo.ui.Draggable(that.element, {
                filter: "." + DRAG_HANDLE_CLASS,
                dragstart: that._onDragStart.bind(that),
                drag: that._onDrag.bind(that),
                dragend: that._onDragEnd.bind(that)
            });
        },

        _configureMode: function() {
            const that = this;
            const options = that.options;
            const popupOptions = that.popup.options;

            if (options.anchor) {
                const map = ANCHOR_POSITION_MAP[options.position] || ANCHOR_POSITION_MAP.top;

                popupOptions.anchor = options.anchor;
                popupOptions.origin = map.origin;
                popupOptions.position = map.position;
            } else {
                const align = $.extend({}, that.options.containerAlign);
                const alignStr = (align.vertical || "top") + " " + (align.horizontal || "center");

                popupOptions.anchor = that._container;
                popupOptions.origin = alignStr;
                popupOptions.position = alignStr;
            }

            popupOptions.collision = "flip fit";
        },

        _refreshLayout: function() {
            const that = this;

            if (!that.toolbar || !that.popup.visible()) {
                return;
            }

            that.toolbar.resize(true);

            if (!that._explicitPosition) {
                that.popup.position();
                that._applyMargin();
            }
        },

        _applyMargin: function() {
            const that = this;
            const margin = $.extend({ horizontal: 0, vertical: 0 }, that.options.margin);

            if (that.popup.wrapper && that.popup.wrapper.length) {
                that.popup.wrapper.css({
                    marginTop: margin.vertical,
                    marginLeft: margin.horizontal
                });
            }
        },

        _currentOffset: function() {
            const that = this;
            const wrapper = that.popup.wrapper;

            if (wrapper && wrapper.length) {
                return {
                    top: parseInt(wrapper.css("top"), 10) || 0,
                    left: parseInt(wrapper.css("left"), 10) || 0
                };
            }

            return that._pos || { top: 0, left: 0 };
        },

        _viewportBounds: function() {
            const that = this;
            const wrapper = that.popup.wrapper;
            const width = wrapper && wrapper.length ? kendo._outerWidth(wrapper) : 0;
            const height = wrapper && wrapper.length ? kendo._outerHeight(wrapper) : 0;

            const win = $(window);
            let originTop = 0;
            let originLeft = 0;

            if (wrapper && wrapper.length && that.popup.visible()) {
                const offset = wrapper.offset();
                const current = that._currentOffset();

                originTop = offset.top - current.top;
                originLeft = offset.left - current.left;
            }

            const minTop = win.scrollTop() - originTop;
            const minLeft = win.scrollLeft() - originLeft;

            return {
                minTop: minTop,
                minLeft: minLeft,
                maxTop: minTop + Math.max(0, window.innerHeight - height),
                maxLeft: minLeft + Math.max(0, window.innerWidth - width)
            };
        },

        _isPositionArg: function(arg) {
            return !!arg && typeof arg === "object" && !arg.nodeType && !arg.jquery &&
                (Object.prototype.hasOwnProperty.call(arg, "top") || Object.prototype.hasOwnProperty.call(arg, "left"));
        },

        show: function(arg) {
            const that = this;

            if (that._isPositionArg(arg)) {
                that.options.anchor = undefined;
                that._configureMode();
                that._explicitPosition = true;
                that.popup.open(arg.left, arg.top);
                that._refreshLayout();
                that._pos = { top: arg.top, left: arg.left };
                return;
            }

            if (arg !== undefined) {
                that.options.anchor = arg;
            }

            that._configureMode();
            that._explicitPosition = false;
            that.popup.open();
            that._refreshLayout();
            that._applyMargin();
        },

        hide: function() {
            const that = this;

            that.popup.close();
        },

        setPosition: function(position) {
            const that = this;
            let top = position.top;
            let left = position.left;

            that._explicitPosition = true;

            if (that.options.draggable) {
                const bounds = that._viewportBounds();

                top = constrain(top, bounds.minTop, bounds.maxTop);
                left = constrain(left, bounds.minLeft, bounds.maxLeft);
            }

            if (that.popup.visible()) {
                that.popup.wrapper.css({ top: top, left: left });
            } else {
                that._explicitPosition = true;
                that.popup.open(left, top);
                that._refreshLayout();
            }

            that._pos = { top: top, left: left };
            that.trigger("move", { top: top, left: left });
        },

        setAnchor: function(target) {
            const that = this;

            that.options.anchor = target;
            that._explicitPosition = false;
            that._configureMode();

            if (that.popup.visible()) {
                that.popup.position();
                that._pos = that._currentOffset();
            }
        },

        focus: function() {
            const that = this;
            const focusable = that.toolbar.element.find(":kendoFocusable").not("[tabindex='-1']").first();

            kendo.focusElement(focusable);
        },

        _keyDown: function(e) {
            const that = this;
            const keyCode = e.keyCode;

            if (!kendo.keyDownHandler(e, that)) {
                return;
            }

            if (!that._moveMode) {
                if (keyCode === M_KEYCODE) {
                    e.preventDefault();
                    that._startMoveMode();
                }
                return;
            }

            if (keyCode === M_KEYCODE) {
                e.preventDefault();

                if (that.dragHandle && e.target !== that.dragHandle[0]) {
                    kendo.focusElement(that.dragHandle);
                } else {
                    that._commitMoveMode();
                }

                return;
            }

            if (keyCode === keys.ESC) {
                e.preventDefault();
                that._cancelMoveMode();
                return;
            }

            if (keyCode === keys.ENTER) {
                e.preventDefault();
                that._commitMoveMode();
                return;
            }

            if (!that.dragHandle || e.target !== that.dragHandle[0]) {
                return;
            }

            let top = that._pos.top;
            let left = that._pos.left;
            let direction = null;

            if (keyCode === keys.UP) {
                top -= MOVE_STEP;
                direction = "up";
            } else if (keyCode === keys.DOWN) {
                top += MOVE_STEP;
                direction = "down";
            } else if (keyCode === keys.LEFT) {
                left -= MOVE_STEP;
                direction = "left";
            } else if (keyCode === keys.RIGHT) {
                left += MOVE_STEP;
                direction = "right";
            } else {
                return;
            }

            e.preventDefault();
            that.setPosition({ top: top, left: left });
            that._announce(direction);
        },

        _startMoveMode: function() {
            const that = this;
            const pos = that._pos;

            if (that.trigger("dragStart", { top: pos.top, left: pos.left })) {
                return;
            }

            that._moveMode = true;
            that._moveOrigin = { top: pos.top, left: pos.left };
            that.dragHandle.attr("aria-pressed", "true");

            if (that.dragHandle[0] !== document.activeElement) {
                kendo.focusElement(that.dragHandle);
            }
        },

        _commitMoveMode: function() {
            const that = this;
            const pos = that._pos;

            that._moveMode = false;
            that.dragHandle.attr("aria-pressed", "false");
            that.trigger("dragEnd", { top: pos.top, left: pos.left });
        },

        _cancelMoveMode: function() {
            const that = this;
            const origin = that._moveOrigin;

            that.popup.wrapper.css({ top: origin.top, left: origin.left });
            that._pos = { top: origin.top, left: origin.left };

            that._moveMode = false;
            that.dragHandle.attr("aria-pressed", "false");
            that.trigger("dragEnd", { top: origin.top, left: origin.left });
        },

        _announce: function(direction) {
            const that = this;
            const messages = that.options.messages;
            const directionMessages = {
                up: messages.movedUp,
                down: messages.movedDown,
                left: messages.movedLeft,
                right: messages.movedRight
            };

            if (that._liveRegion) {
                that._liveRegion.text(directionMessages[direction] || "");
            }
        },

        _onDragStart: function(e) {
            const that = this;
            const pos = that._pos;

            if (that.trigger("dragStart", { top: pos.top, left: pos.left })) {
                e.preventDefault();
                return false;
            }
        },

        _onDrag: function(e) {
            const that = this;
            const top = that._pos.top + e.y.delta;
            const left = that._pos.left + e.x.delta;

            that.setPosition({ top: top, left: left });
        },

        _onDragEnd: function() {
            const that = this;
            const pos = that._pos;

            that.trigger("dragEnd", { top: pos.top, left: pos.left });
        },

        destroy: function() {
            const that = this;

            if (that.dragHandle) {
                that.dragHandle.off(ns);
            }

            if (that._draggable) {
                that._draggable.destroy();
            }

            if (that.toolbar) {
                that.toolbar.destroy();
            }

            if (that.popup) {
                const wrapper = that.popup.wrapper;
                const popupOptions = that.popup.options;

                popupOptions.appendTo = $();

                that.popup.destroy();

                if (wrapper && wrapper.length) {
                    that.element.insertBefore(wrapper);
                    wrapper.remove();
                }
            }

            that.element.off(ns);

            Widget.fn.destroy.call(that);
        }
    });

    kendo.cssProperties.registerPrefix("FloatingToolBar", "k-floating-toolbar-");

    kendo.ui.plugin(FloatingToolBar);
})(window.kendo.jQuery);

export default kendo;
